import React, { useCallback } from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  setConfigPhase,
  setConfigPlan,
  setSelections,
  clearCellOverrides,
  selectConfigPhase,
  selectConfigSelections,
  selectConfigPlan,
  selectConfigPerCell,
  selectConfigWeekPortions,
  selectConfigWindow,
  selectConfigFilling,
} from '../../../redux/mealPlanUISlice';
import {
  setCurrentWeek,
  selectCurrentWeekStart,
} from '../../../redux/mealPlanSlice';
import { selectRecipeIndex } from '../../../redux/recipeIndexSlice';
import { Paprika } from '../../../styles/theme';
import { ConfigDayList } from './ConfigDayList';
import { ConfigActionBarM } from './ConfigActionBarM';
import { GenerationStage, GenerationProgress } from '../GenerationStage';
import { useGenerateWeek, firstRegeneratedDay } from '../useGenerateWeek';
import type { RecipeIndexEntry } from '../../../services/unifiedRecipeService';
import { useTranslation } from 'react-i18next';
import { formatWeekRange } from '../../../utils/dateLocale';
import { addDays } from '../../../utils/localDate';

const shiftWeek = (weekStart: string, delta: number): string =>
  addDays(weekStart, delta * 7);

export function MealConfigMobile() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const configPhase = useAppSelector(selectConfigPhase);
  const selections = useAppSelector(selectConfigSelections);
  const configPlan = useAppSelector(selectConfigPlan);
  const configPerCell = useAppSelector(selectConfigPerCell);
  const configWeekPortions = useAppSelector(selectConfigWeekPortions);
  const recipeIndex = useAppSelector(selectRecipeIndex);
  const weekStart = useAppSelector(selectCurrentWeekStart);
  // Régénérer from the phone runs on the seven days from today (#808): the
  // header names the days it writes (tonight kept, it starts tomorrow, #862),
  // and there is no other week to step to mid-run.
  const configWindow = useAppSelector(selectConfigWindow);
  const configFilling = useAppSelector(selectConfigFilling);
  const rangeStart = configWindow ? firstRegeneratedDay(configFilling, configWindow) : weekStart;
  const rangeEnd = configWindow ? addDays(configWindow, 6) : undefined;
  const canStepWeek = !configWindow || configWindow === weekStart;

  const { generate, cancel } = useGenerateWeek();
  const generating = configPhase === 'thinking' || configPhase === 'cascade';

  const handleRegenerate = useCallback(() => {
    dispatch(setConfigPhase('config'));
  }, [dispatch]);

  const handleCellShuffle = useCallback((sk: number) => {
    const currentId = configPlan[sk];
    const usedIds = new Set(Object.values(configPlan));
    const fresh = recipeIndex.filter((r: RecipeIndexEntry) => r.id !== currentId && !usedIds.has(r.id));
    const pool = fresh.length > 0 ? fresh : recipeIndex.filter((r: RecipeIndexEntry) => r.id !== currentId);
    if (pool.length === 0) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    dispatch(setConfigPlan({ ...configPlan, [sk]: pick.id }));
  }, [dispatch, configPlan, recipeIndex]);

  const handlePortionsTap = useCallback((_sk: number) => {
    // ConfigDayList handles PortionsModal internally via CellSheet
  }, []);

  // Spec: navigating to another week resets the selection and overrides.
  const goWeek = useCallback((delta: number) => {
    dispatch(setCurrentWeek(shiftWeek(weekStart, delta)));
    dispatch(setSelections([]));
    dispatch(clearCellOverrides());
  }, [dispatch, weekStart]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{configPhase === 'config' ? t('mealConfig.planShort') : t('mealConfig.yourSuggestions')}</Text>
        {generating && (
          <View style={styles.status}>
            <Text style={styles.headerSub}>
              {configPhase === 'thinking'
                ? t('mealConfig.affinity.preparing')
                : t('mealConfig.affinity.ready', { count: Object.keys(configPlan).length })}
            </Text>
            <GenerationProgress />
          </View>
        )}
        <View style={styles.weekNav}>
            {canStepWeek && (
              <Pressable style={styles.navBtn} onPress={() => goWeek(-1)}>
                <Text style={styles.navBtnText}>‹</Text>
              </Pressable>
            )}
            <Text style={styles.weekRange} testID="config-range">{formatWeekRange(rangeStart, i18n.language, 'long', rangeEnd)}</Text>
            {canStepWeek && (
              <Pressable style={styles.navBtn} onPress={() => goWeek(1)}>
                <Text style={styles.navBtnText}>›</Text>
              </Pressable>
            )}
        </View>
      </View>
      <GenerationStage compact>
        <ConfigDayList
          phase={configPhase}
          onCellShuffle={handleCellShuffle}
          onPortionsTap={handlePortionsTap}
        />
      </GenerationStage>
      <ConfigActionBarM
        phase={configPhase}
        selCount={selections.length}
        onGenerate={generate}
        onRegenerate={handleRegenerate}
        onCancel={cancel}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 8,
    gap: 12,
  },
  headerTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 26,
    lineHeight: 32,
    color: Paprika.textPrimary,
  },
  status: {
    gap: 8,
    marginTop: -6,
  },
  headerSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    lineHeight: 16,
    color: Paprika.textSecondary,
  },
  weekNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: 14,
    backgroundColor: Paprika.surfaceLowest,
    paddingHorizontal: 6,
    paddingVertical: 6,
  },
  navBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 20,
    color: Paprika.textSecondary,
    lineHeight: 22,
  },
  weekRange: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: Paprika.textPrimary,
  },
});
